/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { ChevronLeft, ChevronRight, FirstPage, LastPage, Refresh } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { FwQueryState } from "../hooks/useFwQuery";
import { useTranslate } from "../hooks/useTranslate/useTranslate";
import { useTailwindMediaQueries } from "../hooks/useTailwindMediaQueries";
import { useThemeState } from "../hooks/useThemeState";

export interface FwQueryPagerProps<T> {
    query: FwQueryState<T>;
    showRefreshButton?: boolean;
    showItemCount?: boolean;
    onPageChange?: (pageNo: number) => void;
}

export function FwQueryPager<T>({ 
    query, 
    showRefreshButton = true, 
    showItemCount = true,
    onPageChange
}: FwQueryPagerProps<T>) {
    const translate = useTranslate();
    const theme = useThemeState().get().theme;
    const mediaQueries = useTailwindMediaQueries();

    return (
        <div css={css`flex:0 0 auto;text-align:center;padding:.25em 0;color:${theme.palette.siteTextPrimary};font-size:.8rem;`}>
            <div css={css`display:flex;flex-direction:row;align-items:center;justify-content:center;`}>
                <IconButton 
                    color="primary" 
                    disabled={query.firstBtnDisabled} 
                    aria-label="first page" 
                    onClick={async e => {
                        if (onPageChange) {
                            onPageChange(1);
                        } else {
                            await query.firstPageAsync();
                        }
                    }}
                >
                    <FirstPage fontSize="large" />
                </IconButton>
                
                <IconButton 
                    color="primary" 
                    disabled={query.prevBtnDisabled} 
                    aria-label="previous page" 
                    onClick={async e => {
                        if (onPageChange) {
                            onPageChange(query.pageNo - 1);
                        } else {
                            await query.prevPageAsync();
                        }
                    }}
                >
                    <ChevronLeft fontSize="large" />
                </IconButton>
                
                <div css={css`display:inline-flex;flex-direction:column;align-items:center; justify-content:center;height:48px;${((!mediaQueries.minw_sm) ? 'padding-top:20px;' : '')}`}>
                    <div css={css`white-space:nowrap;`}>
                        <span>{translate('Page')}: </span>
                        <input
                            aria-label="Page number"
                            type="text"
                            css={css`width:35px;text-align:center;`}
                            disabled={query.pageNoBtnDisabled || query.totalItems === 0}
                            value={query.totalItems === 0 ? '-' : query.pageNo}
                            onChange={async e => {
                                const value = e.currentTarget.value;
                                if (onPageChange) {
                                    onPageChange(+value);
                                } else {
                                    await query.setPageNoAsync(+value, false);
                                }
                            }}
                            onKeyDown={async e => {
                                const value = (e.currentTarget as HTMLInputElement).value;
                                const key = e.key;
                                if (key === 'Enter') {
                                    if (onPageChange) {
                                        onPageChange(+value);
                                    } else {
                                        await query.setPageNoAsync(+value, true);
                                    }
                                }
                            }}
                        />
                        <span> of {query.totalItems === 0 ? '-' : query.totalPages}</span>
                    </div>
                    {showItemCount && !mediaQueries.minw_sm && (
                        <div css={css`white-space:nowrap;font-size:.7rem;padding-top:6px;`}>
                            {query.isPending && query.totalItems === 0 ? 'Loading...' : `${query.totalItems.toLocaleString()} ${translate('items')}`}
                        </div>
                    )}
                </div>

                <IconButton 
                    color="primary" 
                    disabled={query.nextBtnDisabled} 
                    aria-label="next page" 
                    onClick={async e => {
                        if (onPageChange) {
                            onPageChange(query.pageNo + 1);
                        } else {
                            await query.nextPageAsync();
                        }
                    }}
                >
                    <ChevronRight fontSize="large" />
                </IconButton>
                
                <IconButton 
                    color="primary" 
                    disabled={query.lastBtnDisabled} 
                    aria-label="last page" 
                    onClick={async e => {
                        if (onPageChange) {
                            onPageChange(query.totalPages);
                        } else {
                            await query.lastPageAsync();
                        }
                    }}
                >
                    <LastPage fontSize="large" />
                </IconButton>
                
                {showItemCount && mediaQueries.minw_sm && (
                    <div css={css`white-space:nowrap;font-size:.7rem;`}>
                        {query.isPending && query.totalItems === 0 ? 'Loading...' : `${query.totalItems.toLocaleString()} ${translate('items')}`}
                    </div>
                )}

                {showRefreshButton && (
                    <IconButton 
                        color="primary" 
                        aria-label="refresh"
                        onClick={async e => {
                            await query.loadAsync();
                        }}
                    >
                        <Refresh fontSize="large" />
                    </IconButton>
                )}
            </div>
        </div>
    );
} 